import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { 
  MerchantApplication, 
  MerchantApplicationData, 
  ApplicationStatus,
  PipelineStatus 
} from '../types';

export class MerchantApplicationService {
  private static COLLECTION = 'merchantApplications';
  private static PIPELINE_COLUMNS_COLLECTION = 'pipelineColumns';

  // Create or update a merchant application
  static async saveApplication(
    applicationId: string,
    companyId: string,
    userId: string,
    data: Partial<MerchantApplicationData>,
    status: ApplicationStatus = 'in_progress'
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    
    // Add default pricing if not present and this is a new application
    const dataWithDefaults = {
      ...data,
      pricing: data.pricing || {
        online: { processingFee: 3.5, transactionFee: 30 },
        offline: { processingFee: 3.5, transactionFee: 20 }
      }
    };
    
    await setDoc(applicationRef, {
      companyId,
      userId,
      status,
      pipelineStatus: status === 'submitted' ? 'phone' : 'lead',
      data: dataWithDefaults,
      updatedAt: Timestamp.now(),
      createdAt: Timestamp.now() // Only sets on first creation due to merge
    }, { merge: true });
  }

  // Get a single application by ID
  static async getApplication(applicationId: string): Promise<MerchantApplication | null> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    const applicationSnap = await getDoc(applicationRef);
    
    if (!applicationSnap.exists()) {
      return null;
    }
    
    return {
      id: applicationSnap.id,
      ...applicationSnap.data()
    } as MerchantApplication;
  }

  // Get all applications for a company
  static async getCompanyApplications(companyId: string): Promise<MerchantApplication[]> {
    const q = query(
      collection(db, this.COLLECTION),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MerchantApplication));
  }

  // Get all applications (for super admin)
  static async getAllApplications(filters?: {
    status?: ApplicationStatus;
    pipelineStatus?: PipelineStatus;
  }): Promise<MerchantApplication[]> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters?.pipelineStatus) {
      constraints.push(where('pipelineStatus', '==', filters.pipelineStatus));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    const q = query(collection(db, this.COLLECTION), ...constraints);
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MerchantApplication));
  }

  // Update application status
  static async updateApplicationStatus(
    applicationId: string,
    status: ApplicationStatus,
    pipelineStatus?: PipelineStatus
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    const updates: any = {
      status,
      updatedAt: Timestamp.now()
    };
    
    if (pipelineStatus) {
      updates.pipelineStatus = pipelineStatus;
    }
    
    // Add timestamp for specific status changes
    if (status === 'approved') {
      updates['data.approvedAt'] = Timestamp.now();
    } else if (status === 'rejected') {
      updates['data.rejectedAt'] = Timestamp.now();
    } else if (status === 'submitted') {
      updates['data.submittedAt'] = Timestamp.now();
    }
    
    await updateDoc(applicationRef, updates);
  }

  // Update pipeline status
  static async updatePipelineStatus(
    applicationId: string,
    pipelineStatus: PipelineStatus
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    await updateDoc(applicationRef, {
      pipelineStatus,
      updatedAt: Timestamp.now()
    });
  }

  // Submit application for review
  static async submitApplication(applicationId: string): Promise<void> {
    await this.updateApplicationStatus(applicationId, 'submitted', 'phone');
  }

  // Approve application
  static async approveApplication(
    applicationId: string,
    companyId: string,
    wikiPayitSettings: any
  ): Promise<void> {
    // Update application status
    await this.updateApplicationStatus(applicationId, 'approved', 'approved');
    
    // Update company settings with production credentials
    const companyRef = doc(db, 'companies', companyId);
    await updateDoc(companyRef, {
      wikiPayitSettings,
      'merchantApplication.status': 'approved',
      'merchantApplication.approvedAt': Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  }

  // Reject application
  static async rejectApplication(
    applicationId: string,
    reason: string
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    await updateDoc(applicationRef, {
      status: 'rejected',
      pipelineStatus: 'lead',
      'data.rejectionReason': reason,
      'data.rejectedAt': Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  }

  // Get pipeline columns configuration
  static async getPipelineColumns() {
    try {
      const q = query(
        collection(db, this.PIPELINE_COLUMNS_COLLECTION),
        orderBy('position', 'asc')
      );
      const querySnapshot = await getDocs(q);
      console.log("doc.data()" ,querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })))
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching pipeline columns:', error);
      return [];
    }
  }

  // Update pipeline column configuration
  static async updatePipelineColumn(columnId: string, updates: {
    title?: string;
    color?: string;
    position?: number;
  }): Promise<void> {
    const columnRef = doc(db, this.PIPELINE_COLUMNS_COLLECTION, columnId);
    await updateDoc(columnRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }

  // Add bank to merchant application
  static async addBankToApplication(
    applicationId: string,
    bankId: string
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    const applicationSnap = await getDoc(applicationRef);
    
    if (!applicationSnap.exists()) {
      throw new Error('Application not found');
    }
    
    const currentBanks = applicationSnap.data().assignedBanks || [];
    
    if (currentBanks.includes(bankId)) {
      throw new Error('Bank already assigned');
    }
    
    await updateDoc(applicationRef, {
      assignedBanks: [...currentBanks, bankId],
      updatedAt: Timestamp.now()
    });
  }

  // Remove bank from merchant application
  static async removeBankFromApplication(
    applicationId: string,
    bankId: string
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    const applicationSnap = await getDoc(applicationRef);
    
    if (!applicationSnap.exists()) {
      throw new Error('Application not found');
    }
    
    const currentBanks = applicationSnap.data().assignedBanks || [];
    const updatedBanks = currentBanks.filter((id: string) => id !== bankId);
    
    await updateDoc(applicationRef, {
      assignedBanks: updatedBanks,
      updatedAt: Timestamp.now()
    });
  }

  // Update merchant form data
  static async updateApplicationData(
    applicationId: string,
    updates: Partial<MerchantApplicationData>
  ): Promise<void> {
    const applicationRef = doc(db, this.COLLECTION, applicationId);

    // Prepare the update object with nested data path
    const updateObject: any = {
      updatedAt: Timestamp.now()
    };

    // Add data fields with proper nesting
    Object.entries(updates).forEach(([key, value]) => {
      updateObject[`data.${key}`] = value;
    });

    await updateDoc(applicationRef, updateObject);
  }

  // Delete merchant application and clean up company reference
  static async deleteApplication(applicationId: string): Promise<void> {
    // Get the application first to find the company ID
    const applicationRef = doc(db, this.COLLECTION, applicationId);
    const applicationSnap = await getDoc(applicationRef);

    if (!applicationSnap.exists()) {
      throw new Error('Application not found');
    }

    const applicationData = applicationSnap.data();
    const companyId = applicationData.companyId;

    // Delete the application document
    await deleteDoc(applicationRef);

    // Clean up the company document reference
    if (companyId) {
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);

      if (companySnap.exists()) {
        const companyData = companySnap.data();

        // Check if this company's merchantApplication points to the deleted application
        if (companyData.merchantApplication?.applicationId === applicationId) {
          // Remove the merchantApplication field from the company
          await updateDoc(companyRef, {
            merchantApplication: null,
            updatedAt: Timestamp.now()
          });
        }
      }
    }
  }
}